import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import {
  Mail,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  KeyRound,
  Sun,
  Moon
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PATHS } from "./config/paths";
import API from "./api/api01";

// Canvas Particle Network Component (Reusable style)
const ParticleNetwork = ({ isDark }: { isDark: boolean }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let particles: any[] = [];
    const particleCount = 120;
    const connectionDistance = 150;
    const particleSpeed = 0.8;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);
    resize();

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;

      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.vx = (Math.random() - 0.5) * particleSpeed;
        this.vy = (Math.random() - 0.5) * particleSpeed;
        this.radius = Math.random() * 1.5 + 0.5;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > canvas!.width) this.vx = -this.vx;
        if (this.y < 0 || this.y > canvas!.height) this.vy = -this.vy;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? "rgba(100, 150, 255, 0.4)" : "rgba(59, 130, 246, 0.3)";
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            let opacity = 1 - distance / connectionDistance;
            ctx.strokeStyle = isDark
              ? `rgba(100, 150, 255, ${opacity * 0.15})`
              : `rgba(59, 130, 246, ${opacity * 0.2})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDark]);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />;
};

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(true);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "success">("idle");
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    setMouse({ x: e.clientX, y: e.clientY });
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 2000);
      return;
    }

    setStatus("loading");
    try {
      // Simulate/Trigger reset
      await API.post("/auth/forgot-password", { email: email.trim() });
      setStatus("success");
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 2000);
    }
  };

  const variants: Variants = {
    error: {
      x: [0, -8, 8, -6, 6, 0],
      transition: { duration: 0.4 },
    },
    success: { scale: 1, opacity: 1 },
    idle: { scale: 1, opacity: 1 },
    loading: { scale: 1, opacity: 1 },
  };

  return (
    <div
      onMouseMove={handleMouseMove}
      className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-colors duration-700 ${
        isDark ? "bg-[#05070F] text-white" : "bg-gradient-to-br from-slate-100 via-white to-slate-200 text-slate-900"
      }`}
    >
      {/* 🌌 Spotlight */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: isDark
            ? `radial-gradient(700px circle at ${mouse.x}px ${mouse.y}px, rgba(59,130,246,0.15), transparent 45%)`
            : `radial-gradient(700px circle at ${mouse.x}px ${mouse.y}px, rgba(59,130,246,0.12), transparent 45%)`,
        }}
      />

      <ParticleNetwork isDark={isDark} />

      {/* Theme Toggle */}
      <button
        onClick={() => setIsDark(!isDark)}
        className={`absolute top-8 right-8 z-50 p-3 rounded-full backdrop-blur-xl border transition-all duration-300 hover:scale-110 active:scale-95 ${
          isDark ? "bg-white/5 border-white/10 text-white" : "bg-black/5 border-black/10 text-slate-600"
        }`}
      >
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* 🧊 CARD */}
      <motion.div
        variants={variants}
        animate={status}
        className={`relative z-10 w-full max-w-md p-10 rounded-[2.5rem] overflow-hidden transition-all duration-500 ${
          isDark
            ? "backdrop-blur-3xl border border-white/10 bg-[#0a0f1e]/80 shadow-[0_0_80px_rgba(0,0,0,0.7)]"
            : "bg-white/95 border border-slate-200 shadow-2xl"
        }`}
      >
        {/* ✨ Glass reflection */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-20 pointer-events-none" />

        <button
          onClick={() => navigate(PATHS.LOGIN)}
          className={`absolute top-8 left-8 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${
            isDark ? "text-gray-500 hover:text-white" : "text-slate-400 hover:text-slate-900"
          }`}
        >
          <ArrowLeft size={14} /> Back
        </button>

        {/* Header */}
        <div className="flex flex-col items-center mb-10 mt-6">
          <div className={`w-18 h-18 flex items-center justify-center rounded-3xl transition-all duration-500 ${
              status === "success" ? "bg-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.4)]" : "bg-blue-600 shadow-[0_0_20px_rgba(37,99,235,0.4)]"
          }`}>
            {status === "success" ? (
              <CheckCircle2 className="text-white" size={34} />
            ) : (
              <KeyRound className="text-white" size={34} />
            )}
          </div>

          <h2 className="text-2xl font-bold mt-8 tracking-tight">Recovery Node</h2>
          <p className={`text-xs mt-2 text-center max-w-[240px] ${isDark ? "text-gray-400" : "text-slate-500"}`}>
            {status === "success" 
              ? "Instructions have been transmitted to your registered identity."
              : "Enter your secure email to initiate authorization override."}
          </p>
        </div>

        {status !== "success" ? (
          <form onSubmit={handleResetRequest} className="space-y-6">
            <div className="relative group">
              <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-gray-500 group-focus-within:text-blue-400' : 'text-slate-400 group-focus-within:text-blue-600'}`} size={18} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="REGISTERED EMAIL"
                className={`w-full pl-12 pr-4 py-4 rounded-2xl transition-all font-medium tracking-tight ${
                  isDark 
                    ? 'bg-black/40 border border-white/5 text-white placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500/50 outline-none' 
                    : 'bg-slate-100 border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/50 outline-none shadow-inner'
                }`}
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={status === "loading"}
              className={`w-full py-4 rounded-2xl font-black text-xs tracking-[0.2em] uppercase flex justify-center items-center gap-3 transition-all duration-300 ${
                isDark
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-[0_10px_30px_rgba(37,99,235,0.4)]'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-[0_10px_30px_rgba(37,99,235,0.2)]'
              } ${status === 'loading' ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}
            >
              {status === "loading" ? "Processing..." : <>Send Reset Link <ArrowRight size={16} /></>}
            </motion.button>
          </form>
        ) : (
          <div className="space-y-6">
            <div className={`p-4 rounded-2xl border ${isDark ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "bg-emerald-50/50 border-emerald-200 text-emerald-700"} text-xs font-bold text-center uppercase tracking-wider`}>
              Communication Dispatched Successfully
            </div>
            <button
               onClick={() => navigate(PATHS.LOGIN)}
               className={`w-full py-4 rounded-2xl border font-bold text-[10px] uppercase tracking-[0.2em] transition-all ${
                 isDark ? "border-white/10 hover:bg-white/5" : "border-slate-200 hover:bg-slate-50"
               }`}
            >
              Return to Terminal
            </button>
          </div>
        )}

        <footer className="mt-12 text-center opacity-40">
            <p className="text-[9px] font-bold uppercase tracking-[0.4em]">
                XeroAlpha Cyber-Security Protocol
            </p>
        </footer>
      </motion.div>
    </div>
  );
}
